// ---------------------------------------------------------------------------
// Application entry point.
//
// Responsibilities (in order):
//   1. Validate runtime config (config.js's requireEnv throws at module load
//      if a required env var is missing).
//   2. Configure Amplify against the Cognito User Pool.
//   3. Install global unhandled-rejection / error logging — these complement
//      the React ErrorBoundary (which catches render-phase errors) by
//      catching async/Promise errors that React does not see.
//   4. Mount the React tree under ErrorBoundary + QueryClientProvider.
// ---------------------------------------------------------------------------

import React from 'react';
import ReactDOM from 'react-dom/client';
import { Amplify } from 'aws-amplify';
import { QueryClient, QueryClientProvider } from 'react-query';
import { COGNITO_CONFIG } from './config';
import App from './App';
import { ErrorBoundary } from './components/ErrorBoundary';
import './index.css';

// 1. Config validated by importing it (requireEnv throws on missing values).

// 2. Configure Amplify. We pass only the Cognito identity-provider settings;
//    the Dev Portal does not use Amplify Storage / API / DataStore.
Amplify.configure({
  Auth: {
    Cognito: {
      region: COGNITO_CONFIG.region,
      userPoolId: COGNITO_CONFIG.userPoolId,
      userPoolClientId: COGNITO_CONFIG.userPoolClientId,
    },
  },
});

// 3. Global async error logging. Render-phase errors are caught by
//    ErrorBoundary; this covers everything else (rejected Promises from
//    fetch calls, errors thrown inside event handlers that don't bubble,
//    setTimeout/setInterval callback failures).
window.addEventListener('unhandledrejection', (event) => {
  console.error('[dev-portal] unhandled promise rejection', event.reason);
});
window.addEventListener('error', (event) => {
  console.error('[dev-portal] uncaught error', event.error ?? event.message);
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, staleTime: 30_000 },
  },
});

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </ErrorBoundary>
);
